import { Routes, Route, Navigate } from "react-router-dom";
import Layout from "components/Layout";
import PreArrival from "views/PreArrival";
import RoomReady from "views/RoomReady";
import PreAuth from "views/PreAuth";
import Confirmation from "views/Confirmation";
import { useEffect, useRef, useState } from "react";
import Login from "components/Login";
import RemoteControl from "views/RemoteControl";
import { navItemsByHotel } from "../constants";
import Payment from "views/Payment";
import { useAuth } from "@clerk/react";
import { getUserHotels } from "services/hotels";

const hotelIds: { [name: string]: string[] } = {
  mini: ["62ff1bef058c188c80ea0098", "62ff1c16058c188c80ea009a"],
  parklane: ["6311619228fa6745ccf22e56"],
};

function HomeView({ hotel }: { hotel: any }) {
  const hotelName = hotel?.getHotelName()?.toLowerCase()?.replace(/\s/g, "") || "";
  const navItemsByHotelKey =
    Object.keys(navItemsByHotel).find((key) =>
      hotelName.includes(key.toLowerCase())
    ) || "";
  const navItems = navItemsByHotel[navItemsByHotelKey];
  if (navItems?.includes("Room ready")) {
    return <RoomReady hotel={hotel} />;
  }
  return <PreArrival hotel={hotel} />;
}

export default function AllRoutes() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  const hotelRef = useRef<any>(null);
  const [hotel, setHotel] = useState<any>(null);
  const [hotelError, setHotelError] = useState<string | null>(null);

  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  useEffect(() => {
    hotelRef.current = hotel;
  }, [hotel]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      setHotel(null);
      setHotelError(null);
      return;
    }

    let cancelled = false;

    const bootstrap = async () => {
      try {
        const token = await getTokenRef.current();
        if (token) {
          localStorage.setItem("accessToken", token);
        }

        const userHotels = await getUserHotels();
        if (!userHotels.length) {
          throw new Error("No hotels assigned to this user");
        }

        const selected = userHotels[0];
        if (cancelled) return;

        setHotel((prev: any) => {
          if (
            prev?.id === selected.hotelId &&
            prev?.hotelName === selected.hotelName
          ) {
            return prev;
          }
          return {
            id: selected.hotelId,
            hotelName: selected.hotelName,
            getHotelName: () =>
              Object.keys(hotelIds).find((key: string) =>
                hotelIds[key].find((id: string) => selected.hotelId === id)
              ) || selected.hotelName?.toLowerCase(),
          };
        });
        setHotelError(null);
      } catch (err) {
        console.error("Failed to load hotel after Clerk sign-in", err);
        if (!cancelled && !hotelRef.current) {
          setHotel(null);
          setHotelError(
            "Signed in, but no hotel could be loaded for this user."
          );
        }
      }
    };

    bootstrap();

    // Keep Clerk JWT fresh for API calls without remounting the app
    const tokenRefreshId = window.setInterval(async () => {
      try {
        const token = await getTokenRef.current();
        if (token) localStorage.setItem("accessToken", token);
      } catch (err) {
        console.error("Failed to refresh Clerk token", err);
      }
    }, 50_000);

    return () => {
      cancelled = true;
      window.clearInterval(tokenRefreshId);
    };
    // getToken identity changes often in Clerk; use ref instead of depending on it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isSignedIn]);

  if (!isLoaded) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-gray-500">
        Loading...
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (hotelError && !hotel) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 text-center text-sm text-red-700">
        {hotelError}
      </div>
    );
  }

  if (!hotel) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-gray-500">
        Loading hotel...
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route
        path="/room-ready"
        element={
          <Layout hotel={hotel}>
            <RoomReady hotel={hotel} />
          </Layout>
        }
      />
      <Route
        path="/pre-arrival"
        element={
          <Layout hotel={hotel}>
            <PreArrival hotel={hotel} />
          </Layout>
        }
      />
      <Route
        path="/settlement"
        element={
          <Layout hotel={hotel}>
            <PreArrival hotel={hotel} />
          </Layout>
        }
      />
      <Route
        path="/pre-auth"
        element={
          <Layout hotel={hotel}>
            <PreAuth hotel={hotel} />
          </Layout>
        }
      />
      <Route
        path="/payment"
        element={
          <Layout hotel={hotel}>
            <Payment hotel={hotel} />
          </Layout>
        }
      />
      <Route
        path="/confirmation"
        element={
          <Layout hotel={hotel}>
            <Confirmation hotel={hotel} />
          </Layout>
        }
      />
      <Route
        path="/remote-control"
        element={
          <Layout hotel={hotel}>
            <RemoteControl hotel={hotel} />
          </Layout>
        }
      />
      <Route
        path="*"
        element={
          <Layout hotel={hotel}>
            <HomeView hotel={hotel} />
          </Layout>
        }
      />
    </Routes>
  );
}
